"use client";

import { motion } from "framer-motion";
import {
	AlertTriangle,
	Home,
	Lock,
	RefreshCw,
	ServerCrash,
	ShoppingBag,
	WifiOff,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

type ErrorKind = "offline" | "401" | "403" | "404" | "500" | "generic";

function detectKind(
	error: Error & { digest?: string },
	isOnline: boolean,
): ErrorKind {
	if (!isOnline) return "offline";
	const msg = (error.message || "").toLowerCase();
	if (
		msg.includes("networkerror") ||
		msg.includes("failed to fetch") ||
		msg.includes("network request failed") ||
		msg.includes("load failed")
	)
		return "offline";
	if (msg.includes("401") || msg.includes("unauthorized")) return "401";
	if (msg.includes("403") || msg.includes("forbidden")) return "403";
	if (msg.includes("404") || msg.includes("not found")) return "404";
	if (
		msg.includes("500") ||
		msg.includes("internal") ||
		msg.includes("server error") ||
		error.digest
	)
		return "500";
	return "generic";
}

const CONFIG: Record<
	ErrorKind,
	{
		icon: React.ElementType;
		code: string;
		title: string;
		description: string;
		color: string;
	}
> = {
	offline: {
		icon: WifiOff,
		code: "OFFLINE",
		title: "No Internet Connection",
		description:
			"It looks like you're offline. Check your Wi-Fi or mobile data and try again.",
		color: "text-amber-500",
	},
	"401": {
		icon: Lock,
		code: "401",
		title: "Login Required",
		description:
			"You need to be signed in to view this page. Please log in and try again.",
		color: "text-blue-500",
	},
	"403": {
		icon: Lock,
		code: "403",
		title: "Access Denied",
		description:
			"You don't have permission to view this page. If this is a mistake, please contact support.",
		color: "text-orange-500",
	},
	"404": {
		icon: AlertTriangle,
		code: "404",
		title: "Page Not Found",
		description:
			"The page you were looking for doesn't exist or has been moved.",
		color: "text-primary",
	},
	"500": {
		icon: ServerCrash,
		code: "500",
		title: "Server Error",
		description:
			"Our servers ran into an issue. This isn't your fault — we're on it.",
		color: "text-destructive",
	},
	generic: {
		icon: AlertTriangle,
		code: "ERR",
		title: "Something Went Wrong",
		description:
			"An unexpected error occurred while loading this page. Please try again.",
		color: "text-destructive",
	},
};

export default function ErrorPage({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);

	useEffect(() => {
		console.error("Application error:", error);
		document.title = "Error | Ice Age Meals";

		let robotsMeta = document.querySelector(
			'meta[name="robots"]',
		) as HTMLMetaElement | null;
		if (!robotsMeta) {
			robotsMeta = document.createElement("meta");
			robotsMeta.name = "robots";
			document.head.appendChild(robotsMeta);
		}
		robotsMeta.content = "noindex, nofollow";

		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, [error]);

	// Auto-retry once we come back online
	useEffect(() => {
		if (isOnline) reset();
	}, [isOnline, reset]);

	const kind = detectKind(error, isOnline);
	const cfg = CONFIG[kind];
	const Icon = cfg.icon;

	return (
		<div className="min-h-[calc(100vh-16rem)] flex items-center justify-center px-4 py-16">
			<motion.div
				initial={{ opacity: 0, scale: 0.95, y: 20 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				transition={{ duration: 0.5, ease: "easeOut" }}
				className="text-center max-w-lg mx-auto w-full space-y-8"
			>
				{/* Icon */}
				<motion.div
					initial={{ scale: 0.8, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					transition={{ duration: 0.6, delay: 0.1 }}
					className="flex justify-center"
				>
					<div className="relative">
						<div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-destructive/10 flex items-center justify-center">
							<Icon
								className={`w-14 h-14 sm:w-16 sm:h-16 ${cfg.color} opacity-80`}
							/>
						</div>
						<motion.div
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
							className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-background border-2 border-border flex items-center justify-center"
						>
							<span className="text-sm font-black text-muted-foreground">
								!
							</span>
						</motion.div>
					</div>
				</motion.div>

				{/* Text */}
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2, duration: 0.4 }}
					className="space-y-3"
				>
					<p
						className={`text-7xl sm:text-8xl font-black select-none leading-none ${cfg.color} opacity-20`}
					>
						{cfg.code}
					</p>
					<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
						{cfg.title}
					</h1>
					<p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
						{cfg.description}
					</p>
					{error.digest && (
						<p className="text-xs text-muted-foreground/50 font-mono">
							Error ID: {error.digest}
						</p>
					)}
				</motion.div>

				{/* Tips */}
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3, duration: 0.4 }}
					className="bg-secondary/50 rounded-2xl p-5 sm:p-6 space-y-3 text-left"
				>
					<h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
						What you can try
					</h3>
					<ul className="text-sm text-muted-foreground space-y-2">
						{kind === "offline" ? (
							<>
								<li className="flex items-start gap-2">
									<ShoppingBag className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Check your Wi-Fi or mobile data connection</span>
								</li>
								<li className="flex items-start gap-2">
									<RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
									<span>
										Page will automatically reload once you&apos;re back online
									</span>
								</li>
							</>
						) : kind === "401" ? (
							<>
								<li className="flex items-start gap-2">
									<Lock className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Sign in to your account and try again</span>
								</li>
								<li className="flex items-start gap-2">
									<Home className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Return to the homepage and log in from there</span>
								</li>
							</>
						) : kind === "500" ? (
							<>
								<li className="flex items-start gap-2">
									<RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Refresh the page — this is usually temporary</span>
								</li>
								<li className="flex items-start gap-2">
									<ServerCrash className="w-4 h-4 mt-0.5 shrink-0" />
									<span>
										Our team has been notified and is working on a fix
									</span>
								</li>
							</>
						) : (
							<>
								<li className="flex items-start gap-2">
									<RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Refresh the page or click the button below</span>
								</li>
								<li className="flex items-start gap-2">
									<Home className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Go back to the homepage and navigate again</span>
								</li>
								<li className="flex items-start gap-2">
									<ShoppingBag className="w-4 h-4 mt-0.5 shrink-0" />
									<span>Check your internet connection</span>
								</li>
							</>
						)}
					</ul>
				</motion.div>

				{/* Actions */}
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.4, duration: 0.4 }}
					className="flex flex-col sm:flex-row items-center justify-center gap-3"
				>
					{kind !== "403" && kind !== "404" && (
						<Button
							size="lg"
							onClick={reset}
							className="gap-2 w-full sm:w-auto"
						>
							<RefreshCw className="w-4 h-4" />
							Try Again
						</Button>
					)}
					{kind === "401" ? (
						<Button size="lg" className="gap-2 w-full sm:w-auto" asChild>
							<Link href="/api/auth/login">
								<Lock className="w-4 h-4" />
								Sign In
							</Link>
						</Button>
					) : null}
					<Button
						variant="outline"
						size="lg"
						className="gap-2 w-full sm:w-auto"
						asChild
					>
						<Link href="/">
							<Home className="w-4 h-4" />
							Go to Homepage
						</Link>
					</Button>
				</motion.div>
			</motion.div>
		</div>
	);
}
